// Copyright (c) Bentley Systems

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Checkbox,
  ComboBox,
  DefaultCell,
  ProgressRadial,
  Table,
  Text,
} from "@itwin/itwinui-react";
import {
  ExtractedVariable,
  VariableValidationRule,
  VariableValidationType,
} from "./Helpers/xml-variable-extractor";
import { IntegrationProjectStage } from "./Models/integration-project-stage";
import LabeledInputComponent from "../CustomInputComponents/TextInputComponents/LabeledInputComponent";
import { EfFieldValidationTypeEnum } from "../Helpers/enumerators";
import { CellRendererProps, Column, Row } from "@itwin/itwinui-react/react-table";

type VariableValidationEditorProps = {
  variables: ExtractedVariable[];
  validationRules: VariableValidationRule[];
  onChange: (rules: VariableValidationRule[]) => void;
  isEditable: boolean;
  isLoading: boolean;
  projectStage: IntegrationProjectStage;
  reservedVariables?: ExtractedVariable[];
  onValidityChange?: (valid: boolean) => void;
};

type VariableTableRow = {
  name: string;
  dataType: string;
  defaultPreview: string | undefined;
  validationTypeId: string | undefined;
  isRequired: boolean;
  rule: VariableValidationRule;
};

type RegexPresetKey = "letters" | "numbers" | "spaces" | "dashes" | "underscores" | "custom";

type RegexSelectionState = {
  selectedPresets: RegexPresetKey[];
  customRegex: string;
};

const regexPresetOptions: Array<{ key: RegexPresetKey; label: string; fragment: string }> = [
  { key: "letters", label: "Allow letters (A-Z)", fragment: "[A-Za-z]" },
  { key: "numbers", label: "Allow numbers (0-9)", fragment: "[0-9]" },
  { key: "spaces", label: "Allow spaces", fragment: "\\s" },
  { key: "dashes", label: "Allow dashes (-)", fragment: "-" },
  { key: "underscores", label: "Allow underscores (_)", fragment: "_" },
  { key: "custom", label: "Custom regex", fragment: "" },
];

const getSelectedRegexPresets = (regexPattern?: string): RegexPresetKey[] => {
  if (!regexPattern) {
    return [];
  }

  const presetKeys = regexPresetOptions
    .filter((opt) => opt.key !== "custom")
    .map((opt) => opt.key);

  for (let mask = 1; mask < (1 << presetKeys.length); mask += 1) {
    const selectedPresets = presetKeys.filter((_, index) => (mask & (1 << index)) !== 0);
    if (buildRegexPatternFromPresets(selectedPresets) === regexPattern) {
      return selectedPresets;
    }
  }

  return [];
};

const buildRegexPatternFromPresets = (selectedPresets: RegexPresetKey[], customRegex?: string): string => {
  const standardPresetKeys = selectedPresets.filter((preset) => preset !== "custom");
  const includeCustomRegex = selectedPresets.includes("custom") && Boolean(customRegex?.trim());

  if (standardPresetKeys.length === 0 && !includeCustomRegex) {
    return "";
  }

  const fragments = standardPresetKeys
    .map((key) => regexPresetOptions.find((opt) => opt.key === key)?.fragment)
    .filter((fragment): fragment is string => Boolean(fragment));

  const normalizedCustomRegex = customRegex?.trim() ?? "";
  if (includeCustomRegex) {
    if (fragments.length === 0) {
      return normalizedCustomRegex;
    }
    fragments.push(`(?:${normalizedCustomRegex})`);
  }

  if (fragments.length === 0) {
    return "";
  }

  return `^(?:${fragments.join("|")})+$`;
};

const parseRegexSelectionState = (regexPattern?: string): RegexSelectionState => {
  const selectedPresets = getSelectedRegexPresets(regexPattern);
  const builtFromPresets = buildRegexPatternFromPresets(selectedPresets);

  if (regexPattern && regexPattern.trim() !== "" && regexPattern !== builtFromPresets) {
    return {
      selectedPresets: [...selectedPresets, "custom"],
      customRegex: regexPattern,
    };
  }

  return {
    selectedPresets,
    customRegex: "",
  };
};

const VariableValidationEditor = (props: VariableValidationEditorProps) => {
  const { validationRules, onChange, onValidityChange, isEditable, projectStage, variables, reservedVariables, isLoading } = props;
  const expandedRowsRef = useRef<Record<string, boolean>>({});
  const [regexSelections, setRegexSelections] = useState<Record<string, RegexSelectionState>>({});
  const updateRule = useCallback((variableName: string, changes: Partial<VariableValidationRule>) => {
    const exists = validationRules.some((r) => r.variableName === variableName);
    if (exists) {
      const updated = validationRules.map((r) =>
        r.variableName === variableName ? { ...r, ...changes } : r
      );
      onChange(updated);
    } else {
      const newRule: VariableValidationRule = {
        variableName,
        validationTypeId: VariableValidationType.None,
        isRequired: false,
        ...changes,
      };
      onChange([...validationRules, newRule]);
    }
  }, [validationRules, onChange]);

  const handleValidationTypeChange = useCallback((row: Row<VariableTableRow>, selectedValue: unknown) => {
    const rowId = String(row.id);
    const nextTypeRaw = Array.isArray(selectedValue) ? selectedValue[0] : selectedValue;
    const nextType = (nextTypeRaw as VariableValidationType) ?? VariableValidationType.None;
    const currentType = row.original.rule.validationTypeId;

    if (currentType === VariableValidationType.None && nextType !== VariableValidationType.None) {
      expandedRowsRef.current = {
        ...expandedRowsRef.current,
        [rowId]: true,
      };
    }

    if (currentType !== VariableValidationType.None && nextType === VariableValidationType.None) {
      const updatedExpanded = { ...expandedRowsRef.current };
      delete updatedExpanded[rowId];
      expandedRowsRef.current = updatedExpanded;
    }

    updateRule(row.original.name, { validationTypeId: nextType });
  }, [updateRule]);

  const validationTypeOptions = useMemo(() => Object.values(VariableValidationType)
    .filter((v) => v !== VariableValidationType.None || projectStage === IntegrationProjectStage.Development)
    .map((v) => ({ value: v, label: v })), [projectStage]);

  useEffect(() => {
    setRegexSelections((prev) => {
      const next: Record<string, RegexSelectionState> = { ...prev };
      let hasChanges = false;

      validationRules.forEach((rule) => {
        if (rule.validationTypeId !== VariableValidationType.String) {
          return;
        }

        const parsed = parseRegexSelectionState(rule.regexPattern);
        const current = next[rule.variableName];
        const shouldPreservePendingCustomSelection =
          rule.regexPattern !== undefined &&
          rule.regexPattern.trim() === "" &&
          current?.selectedPresets.includes("custom");

        if (shouldPreservePendingCustomSelection) {
          return;
        }

        if (
          !current ||
          current.customRegex !== parsed.customRegex ||
          current.selectedPresets.join("|") !== parsed.selectedPresets.join("|")
        ) {
          next[rule.variableName] = parsed;
          hasChanges = true;
        }
      });

      return hasChanges ? next : prev;
    });
  }, [validationRules]);

  const tableData = useMemo<VariableTableRow[]>(() => {
    return variables.map((v) => {
      const rule = validationRules.find((r) => r.variableName === v.name) ?? {
        variableName: v.name,
        validationTypeId: VariableValidationType.None,
        isRequired: false,
      };
      return {
        name: v.name,
        dataType: v.dataType,
        defaultPreview: v.defaultPreview,
        validationTypeId: rule.validationTypeId,
        isRequired: rule.isRequired,
        rule,
      };
    });
  }, [variables, validationRules]);

  const areRulesValid = useMemo(() => {
    return validationRules
      .filter((r) => r.validationTypeId !== VariableValidationType.None)
      .every((r) => {
        if (r.validationTypeId === VariableValidationType.String) {
          return (
            r.maxLength !== undefined &&
            r.maxLength > 0 &&
            r.regexPattern !== undefined &&
            r.regexPattern.trim() !== ""
          );
        }
        if (r.validationTypeId === VariableValidationType.Enum) {
          return r.allowedValues !== undefined && r.allowedValues.trim() !== "";
        }
        if (r.validationTypeId === VariableValidationType.Date || r.validationTypeId === VariableValidationType.DateTime) {
          return r.dateFormat !== undefined && r.dateFormat.trim() !== "";
        }
        return true;
      });
  }, [validationRules]);

  useEffect(() => {
    onValidityChange?.(areRulesValid);
  }, [areRulesValid, onValidityChange]);

  const subComponent = useCallback((row: Row<VariableTableRow>) => {
    const { rule } = row.original;
    const variableName = row.original.name;
    const regexSelection = regexSelections[variableName] ?? parseRegexSelectionState(rule.regexPattern);
    const isCustomRegexSelected = regexSelection.selectedPresets.includes("custom");
    return (
      <div style={{ padding: 16, width: "100%" }}>
        {row.original.defaultPreview && (
          <div style={{ marginBottom: 8 }}>
            <LabeledInputComponent
              displayStyle="default"
              label="Default Value:"
              validationType={EfFieldValidationTypeEnum.Name150}
              placeholder="Enter text here..."
              defaultValue={row.original.defaultPreview}
              disabled
            />
          </div>
        )}
        {rule.validationTypeId === VariableValidationType.String && (
          <>
            <div style={{ display: "flex", gap: 16 }}>
              <div style={{ flex: 1 }}>
                <LabeledInputComponent
                  label="Max length"
                  validationType={EfFieldValidationTypeEnum.IntType}
                  defaultValue={rule.maxLength?.toString()}
                  onChange={(e) => updateRule(variableName, { maxLength: Number(e.target.value) })}
                  disabled={!isEditable}
                  required={isEditable}
                />
              </div>
              <div style={{ flex: 1 }}>
                <div className="becs-input-grid" style={{ width: "100%" }}>
                  <div className="becs-input-label required">Regex options</div>
                </div>
                <div
                  style={{
                    border: "1px solid var(--iui-color-border)",
                    borderRadius: 4,
                    padding: "8px 10px",
                    display: "flex",
                    flexDirection: "column",
                    gap: 8,
                  }}
                >
                  {regexPresetOptions
                    .filter((opt) => opt.key === "custom" || !isCustomRegexSelected)
                    .map((opt) => {
                    const isChecked = regexSelection.selectedPresets.includes(opt.key);

                    return (
                      <Checkbox
                        key={opt.key}
                        label={opt.label}
                        checked={isChecked}
                        disabled={!isEditable}
                        onChange={(e) => {
                          const currentSelection = regexSelections[variableName] ?? parseRegexSelectionState(rule.regexPattern);
                          let nextSelection: RegexPresetKey[];

                          if (opt.key === "custom") {
                            nextSelection = e.target.checked ? ["custom"] : [];
                          } else {
                            nextSelection = e.target.checked
                              ? Array.from(new Set<RegexPresetKey>([...currentSelection.selectedPresets.filter((selectedKey) => selectedKey !== "custom"), opt.key]))
                              : currentSelection.selectedPresets.filter((selectedKey) => selectedKey !== opt.key && selectedKey !== "custom");
                          }

                          const nextCustomRegex = nextSelection.includes("custom")
                            ? currentSelection.customRegex
                            : "";

                          setRegexSelections((prev) => ({
                            ...prev,
                            [variableName]: {
                              selectedPresets: nextSelection,
                              customRegex: nextCustomRegex,
                            },
                          }));

                          updateRule(variableName, {
                            regexPattern: buildRegexPatternFromPresets(nextSelection, nextCustomRegex),
                          });
                        }}
                      />
                    );
                  })}
                </div>
                {isCustomRegexSelected && (
                  <div style={{ marginTop: 8 }}>
                    <LabeledInputComponent
                      label="Custom regex"
                      validationType={EfFieldValidationTypeEnum.Default}
                      placeholder="e.g. ^[A-Za-z0-9_-]+$"
                      defaultValue={regexSelection.customRegex}
                      onChange={(e) => {
                        const nextCustomRegex = e.target.value;
                        const nextSelection: RegexPresetKey[] = ["custom"];

                        setRegexSelections((prev) => ({
                          ...prev,
                          [variableName]: {
                            selectedPresets: nextSelection,
                            customRegex: nextCustomRegex,
                          },
                        }));

                        updateRule(variableName, {
                          regexPattern: buildRegexPatternFromPresets(nextSelection, nextCustomRegex),
                        });
                      }}
                      disabled={!isEditable}
                      required={isEditable}
                    />
                  </div>
                )}
                {isEditable && regexSelection.selectedPresets.length === 0 && (
                  <Text role="alert" style={{ color: "var(--iui-color-text-negative)", marginTop: 6 }}>
                    Select at least one regex option.
                  </Text>
                )}
                {isEditable &&
                  isCustomRegexSelected &&
                  !regexSelection.customRegex.trim() && (
                    <Text role="alert" style={{ color: "var(--iui-color-text-negative)", marginTop: 6 }}>
                      Enter a custom regex value.
                    </Text>
                  )}
              </div>
              <div style={{ flex: 1 }}>
                <LabeledInputComponent
                  label="Regex description"
                  validationType={EfFieldValidationTypeEnum.Default}
                  defaultValue={rule.regexDescription}
                  onChange={(e) => updateRule(variableName, { regexDescription: e.target.value })}
                  disabled={!isEditable}
                />
              </div>
            </div>
          </>
        )}

        {rule.validationTypeId === VariableValidationType.Number && (
          <>
            <div style={{ display: "flex", gap: 16 }}>
              <div style={{ flex: 1 }}>
                <LabeledInputComponent
                  label="Min value"
                  validationType={EfFieldValidationTypeEnum.FloatType}
                  defaultValue={rule.minValue?.toString()}
                  onChange={(e) => updateRule(variableName, { minValue: Number(e.target.value) })}
                  disabled={!isEditable}
                />
              </div>
              <div style={{ flex: 1 }}>
                <LabeledInputComponent
                  label="Max value"
                  validationType={EfFieldValidationTypeEnum.FloatType}
                  defaultValue={rule.maxValue?.toString()}
                  onChange={(e) => updateRule(variableName, { maxValue: Number(e.target.value) })}
                  disabled={!isEditable}
                />
              </div>
              <div style={{ flex: 1, paddingTop: 16 }}>
                <Checkbox
                  label="Allow decimals"
                  checked={rule.allowDecimals ?? false}
                  onChange={(e) => updateRule(variableName, { allowDecimals: e.target.checked })}
                  disabled={!isEditable}
                />
              </div>
            </div>
          </>

        )}

        {(rule.validationTypeId === VariableValidationType.Date ||
          rule.validationTypeId === VariableValidationType.DateTime) && (
            <LabeledInputComponent
              label="Date format"
              validationType={EfFieldValidationTypeEnum.Default}
              placeholder={
                rule.validationTypeId === VariableValidationType.Date
                  ? "yyyy-MM-dd"
                  : "yyyy-MM-ddTHH:mm:ss"
              }
              defaultValue={rule.dateFormat}
              onChange={(e) => updateRule(variableName, { dateFormat: e.target.value })}
              disabled={!isEditable}
            />
          )}

        {rule.validationTypeId === VariableValidationType.Enum && (
          <LabeledInputComponent
            label="Allowed values (comma-separated)"
            validationType={EfFieldValidationTypeEnum.Default}
            defaultValue={rule.allowedValues}
            onChange={(e) => updateRule(variableName, { allowedValues: e.target.value })}
            disabled={!isEditable}
            required={isEditable}
          />
        )}
      </div>
    );
  }, [updateRule, isEditable, regexSelections]);

  //Reserved variable warning: If the extraction found reserved variables in the XML, show an Alert above the variable list:
  //| Name        | Type   | Default Value        | Required | Validation | Actions |
  const columns = useMemo<Column<VariableTableRow>[]>(() => [
    {
      Header: () => <div style={{ textAlign: "center", width: "100%" }}>Variable</div>,
      accessor: "name",
      minWidth: 125,
      cellRenderer: (r: CellRendererProps<VariableTableRow>) => (
        <DefaultCell {...r} style={{ justifyContent: "center" }} />
      ),
    },
    {
      Header: () => <div style={{ textAlign: "center", width: "100%" }}>Type</div>,
      accessor: "dataType",
      minWidth: 100,
      cellRenderer: (r: CellRendererProps<VariableTableRow>) => (
        <DefaultCell {...r} style={{ justifyContent: "center" }} />
      ),
    },
    {
      Header: () => <div style={{ textAlign: "center", width: "100%" }}>Required</div>,
      accessor: "isRequired",
      minWidth: 100,
      maxWidth: 120,
      cellRenderer: (r: CellRendererProps<VariableTableRow>) => (
        <DefaultCell {...r} style={{ justifyContent: "center" }}>
          <Checkbox
            label=" "
            checked={r.cellProps.value ?? false}
            onChange={(e) => updateRule(r.cellProps.row.original.name, { isRequired: e.target.checked })}
            disabled={!isEditable}
          />
        </DefaultCell>
      ),
    },
    {
      Header: () => <div style={{ textAlign: "center", width: "100%" }}>Validation Type</div>,
      accessor: "validationTypeId",
      minWidth: 200,
      cellRenderer: (r: CellRendererProps<VariableTableRow>) => (
        <DefaultCell {...r} style={{ justifyContent: "center" }}>
          <ComboBox
            options={validationTypeOptions}
            value={r.cellProps.row.original.rule.validationTypeId}
            onChange={(val) => handleValidationTypeChange(r.cellProps.row, val)}
          />
        </DefaultCell>
      ),
    },
  ], [updateRule, isEditable, validationTypeOptions, handleValidationTypeChange]);

  if (isLoading) {
    return <ProgressRadial indeterminate />;
  }

  if (variables.length === 0) {
    return <Text>No configurable variables found in this package.</Text>;
  }

  return (
    <div style={{ paddingBlock: "2px", fontSize: "var(--iui-font-size-0)", width: "100%" }}>
      {reservedVariables && reservedVariables.length > 0 && (
        <Alert type="informational">
          The following variables are system-reserved and cannot be configured:{" "}
          <strong>{reservedVariables.map((v) => v.name).join(", ")}</strong>
          {" "}These variables are managed automatically and will be ignored if submitted by the caller.
        </Alert>
      )}

      <Table
        columns={columns}
        data={tableData}
        emptyTableContent="No configurable variables found."
        isLoading={isLoading}
        subComponent={subComponent}
        density="extra-condensed"
        stateReducer={(newState, action) => {
          if (action.type === "toggleRowExpanded") {
            expandedRowsRef.current = newState.expanded ?? {};
            return newState;
          }
          return { ...newState, expanded: expandedRowsRef.current };
        }}
        style={{ width: "100%" }}
      />
    </div>
  );
};

export default VariableValidationEditor;
